// @flow @format
import {handleActions} from 'lib/redux-actions';

import type {DownloadSummary} from 'lib/downloader';

export type State = {
	entities: {[id: ?string]: DownloadSummary},
	subscribed: boolean,
	grantedBytes: number,
	usedBytes: number,
	throughput: number,
};

export type AsyncDownloadAction = {
	type: string,
	id: string,
};

export type CreateDownloadAction = {
	type: string,
	id: string,
	repositoryId: string,
};

type AddDownloadAction = {
	type: string,
	id: string,
	payload: DownloadSummary,
};

type UpdateDownloadAction = {
	type: string,
	id: string,
	payload: DownloadSummary,
};

type RemoveDownloadAction = {
	id: string,
	type: string,
};

type DownloadSubscribedAction = {
	type: string,
};

type DownloadUpdateUsageAction = {
	type: string,
	payload: {grantedBytes: number, usedBytes: number},
};

type SetThroughtputAction = {
	type: string,
	payload: {throughput: number},
};

// async actions
export const CREATE_DOWNLOAD = 'CREATE_DOWNLOAD';
export const DELETE_DOWNLOAD = 'DELETE_DOWNLOAD';
export const START_DOWNLOAD = 'START_DOWNLOAD';
export const PAUSE_DOWNLOAD = 'PAUSE_DOWNLOAD';
export const SAVE_DOWNLOAD = 'SAVE_DOWNLOAD';
export const SET_THROUGHTPUT = 'SET_THROUGHTPUT';

const makeAsyncAction = (type: string, id: string): AsyncDownloadAction => ({
	type,
	id,
});

export const createDownload = (
	id: string,
	repositoryId: string
): CreateDownloadAction => ({
	id,
	repositoryId,
	type: CREATE_DOWNLOAD,
});

export const deleteDownload = (id: string): AsyncDownloadAction =>
	makeAsyncAction(DELETE_DOWNLOAD, id);
export const saveDownload = (id: string): AsyncDownloadAction =>
	makeAsyncAction(SAVE_DOWNLOAD, id);
export const startDownload = (id: string): AsyncDownloadAction =>
	makeAsyncAction(START_DOWNLOAD, id);
export const pauseDownload = (id: string): AsyncDownloadAction =>
	makeAsyncAction(PAUSE_DOWNLOAD, id);

// actions
export const ADD_DOWNLOAD = 'ADD_DOWNLOAD';
export const UPDATE_DOWNLOAD = 'UPDATE_DOWNLOAD';
export const REMOVE_DOWNLOAD = 'REMOVE_DOWNLOAD';
export const SUBSCRIBE_DOWNLOADS = 'SUBSCRIBE_DOWNLOADS';
export const UPDATE_DOWNLOADS_USAGE = 'UPDATE_DOWNLOADS_USAGE';

export const addDownload = (
	id: string,
	download: DownloadSummary
): AddDownloadAction => ({
	id,
	type: ADD_DOWNLOAD,
	payload: download,
});

export const updateDownload = (
	id: string,
	download: DownloadSummary
): UpdateDownloadAction => ({
	id,
	type: UPDATE_DOWNLOAD,
	payload: download,
});

export const removeDownload = (id: string): RemoveDownloadAction => ({
	id,
	type: REMOVE_DOWNLOAD,
});

export const subscribeDownloads = (): DownloadSubscribedAction => ({
	type: SUBSCRIBE_DOWNLOADS,
});

export const updateDownloadsUsage = (
	grantedBytes: number,
	usedBytes: number
): DownloadUpdateUsageAction => ({
	type: UPDATE_DOWNLOADS_USAGE,
	payload: {grantedBytes, usedBytes},
});

export const setThroughput = (throughput: number) => ({
	type: SET_THROUGHTPUT,
	payload: {throughput},
});

export const initialState: State = {
	entities: {},
	subscribed: false,
	grantedBytes: 0,
	usedBytes: 0,
	throughput: 0,
};

const reducer = handleActions(
	{
		[UPDATE_DOWNLOADS_USAGE]: (
			state: State,
			action: DownloadUpdateUsageAction
		) => ({
			...state,
			...action.payload,
		}),
		[SUBSCRIBE_DOWNLOADS]: (state: State): State => ({
			...state,
			subscribed: true,
		}),
		[ADD_DOWNLOAD]: (state: State, action: AddDownloadAction): State => {
			const entities = {...state.entities};

			entities[action.id] = action.payload;

			return {...state, entities};
		},
		[UPDATE_DOWNLOAD]: (
			state: State,
			action: UpdateDownloadAction
		): State => {
			const entities = {...state.entities};

			entities[action.id] = action.payload;

			return {...state, entities};
		},
		[REMOVE_DOWNLOAD]: (
			state: State,
			action: RemoveDownloadAction
		): State => {
			const entities = {...state.entities};

			delete entities[action.id];

			return {...state, entities};
		},
		[SET_THROUGHTPUT]: (
			state: State,
			action: SetThroughtputAction
		): State => {
			return {
				...state,
				throughput: action.payload.throughput,
			};
		},
	},
	initialState
);

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/entities/downloads.js